// Libraries
import { useRef, useState, useEffect } from 'react';
import styled from 'styled-components';
import { useResizeDetector } from 'react-resize-detector';
import PropTypes from 'prop-types';

// Quill text editor
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

// Dependencies
import { sanitizeQuillContent } from './utils';

const defaultModules = {
  toolbar: [
    ['bold', 'italic', { list: 'bullet' }, { list: 'ordered' }, { align: [] }, 'link', 'clean'],
  ],
};

const defaultFormats = ['bold', 'italic', 'list', 'bullet', 'link', 'align'];

const RichTextEditor = ({
  value = '',
  placeholder = undefined,
  onChange,
  modules = defaultModules,
  formats = defaultFormats,
  containerMinHeight = 150, // Min heigh of the editor area (not including the toolbar)
  ...rest
}) => {
  const [textValue, setTextValue] = useState(value);
  const [toolbarHeight, setToolbarHeight] = useState(0);

  useEffect(() => {
    setTextValue(value || '');
  }, [value]);

  // We dinamically calculate the height of the toolbar to control the height of the editor component.
  // Especially useful when editor is placed horizontally next to another container.
  const quillContainerRef = useRef(null);
  const toolbarRef = useRef(null);

  const { ref: resizeRef } = useResizeDetector({
    onResize: (_width, height) => {
      if (height !== undefined) {
        setToolbarHeight(height);
      }
    },
  });

  useEffect(() => {
    if (quillContainerRef.current) {
      const toolbarNode = quillContainerRef.current.querySelector('.ql-toolbar');
      if (toolbarNode) {
        toolbarRef.current = toolbarNode;
        resizeRef(toolbarNode);
        setToolbarHeight(toolbarNode.offsetHeight);
      }
    }
  }, [resizeRef]);

  const handleValueChange = newValue => {
    setTextValue(newValue);
    onChange(sanitizeQuillContent(newValue));
  };

  return (
    <div ref={quillContainerRef}>
      <StyledQuillEditor
        defaultValue={value}
        value={textValue}
        onChange={handleValueChange}
        modules={modules}
        formats={formats}
        placeholder={placeholder}
        toolbarHeight={toolbarHeight}
        containerMinHeight={containerMinHeight}
        {...rest}
      />
    </div>
  );
};

const StyledQuillEditor = styled(ReactQuill)`
  height: ${({ toolbarHeight }) => `calc(100% - ${toolbarHeight}px)`};

  .ql-toolbar.ql-snow .ql-formats {
    margin: 0;
  }

  b,
  strong {
    font-weight: 600;
  }

  .ql-container {
    min-height: ${({ containerMinHeight }) => `${containerMinHeight}px`};
    font-family: 'Source Sans 3', 'Roboto', sans-serif;
    font-size: 15px;
  }

  .ql-editor {
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;

    &.ql-blank::before {
      font-weight: 100;
      font-style: italic;
      color: ${({ theme }) => theme.grey};
    }
  }
`;

RichTextEditor.propTypes = {
  value: PropTypes.string,
  placeholder: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  modules: PropTypes.instanceOf(Object),
  formats: PropTypes.instanceOf(Array),
  containerMinHeight: PropTypes.number,
};

export default RichTextEditor;
